import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useClient } from './ClientContext';
import { apiCall } from '../services/realApi';
import { demoModeManager } from '../services/demoModeManager';
import type { ProjectSummary } from '../types/projectSummary';

const DEMO_SUPPORTED_PROJECT_IDS = new Set([
  'p0000000-0000-4000-a000-000000000001',
  'p0000000-0000-4000-a000-000000000002',
  'p0000000-0000-4000-a000-000000000003',
]);

interface ProjectContextValue {
  selectedProjectId: string | null;
  projects: ProjectSummary[];
  isLoadingProjects: boolean;
  selectProject: (projectId: string | null) => void;
  hasProjectAccess: boolean;
}

const PROJECT_STORAGE_KEY = 'selectedProjectId';

const ProjectContext = createContext<ProjectContextValue | null>(null);

export const useProject = (): ProjectContextValue => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { selectedClientId } = useClient();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(PROJECT_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  // State 2 users only (not anonymous, not assessment_only)
  const hasProjectAccess = useMemo(() => {
    if (!user) return false;
    if (user.is_anonymous) return false;
    if (user.client?.subscription_plan === 'assessment_only') return false;
    return true;
  }, [user]);

  // Determine the effective client ID for fetching projects
  const effectiveClientId = useMemo(() => {
    if (!user) return null;
    // Client users have their own client_id from their profile
    if (user.role === 'client') return user.client?.id ?? null;
    // Other roles use the selected client from ClientSelector
    return selectedClientId;
  }, [user, selectedClientId]);

  // Fetch projects when effective client changes
  const loadProjects = useCallback(async () => {
    if (!hasProjectAccess || !effectiveClientId) {
      setProjects([]);
      return;
    }

    setIsLoadingProjects(true);
    try {
      const endpoint = user?.role === 'client'
        ? '/projects'
        : `/projects?client_id=${effectiveClientId}`;
      const data = await apiCall(endpoint);
      const rawProjectList: ProjectSummary[] = Array.isArray(data) ? data : [];
      const projectList = demoModeManager.getDemoMode().isDemoMode
        ? rawProjectList.filter((project) => DEMO_SUPPORTED_PROJECT_IDS.has(project.id))
        : rawProjectList;
      setProjects(projectList);

      // Validate stored project ID against loaded list
      const storedId = localStorage.getItem(PROJECT_STORAGE_KEY);
      if (storedId) {
        const isValid = projectList.some(p => p.id === storedId);
        if (!isValid) {
          localStorage.removeItem(PROJECT_STORAGE_KEY);
          setSelectedProjectId(null);
        }
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setProjects([]);
    } finally {
      setIsLoadingProjects(false);
    }
  }, [hasProjectAccess, effectiveClientId, user?.role]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Reset to "All Projects" when client changes (non-client users)
  useEffect(() => {
    if (user?.role !== 'client') {
      setSelectedProjectId(null);
      localStorage.removeItem(PROJECT_STORAGE_KEY);
    }
  }, [selectedClientId, user?.role]);

  const selectProject = useCallback((projectId: string | null) => {
    setSelectedProjectId(projectId);
    if (projectId) {
      localStorage.setItem(PROJECT_STORAGE_KEY, projectId);
    } else {
      localStorage.removeItem(PROJECT_STORAGE_KEY);
    }
  }, []);

  const value: ProjectContextValue = {
    selectedProjectId,
    projects,
    isLoadingProjects,
    selectProject,
    hasProjectAccess,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};
